/**
 * Concrete wiring for the CMMD user settings sync: binds the pure core in
 * userSettingsSync.ts to cmmdApiClient, the server-state store, and the
 * primary environment connection, and exposes the root-layout hook.
 *
 * Namespace mapping:
 *   ui-settings ↔ the server's effective settings (ServerConfig.settings)
 *   keybindings ↔ the sparse custom keybinding rules (what keybindings.json
 *                 stores; defaults are re-derived per machine, never synced)
 *
 * All postures run this — desktop, cloud shell, sprite, local dev — because
 * every posture serves the same web app. When CMMD is not connected the hook
 * does nothing and the local files remain authoritative, which is the
 * fallback the cloud-shell-only path used to make universal.
 */
import { Schema } from "effect";

import {
  KeybindingCommand,
  type CmmdUserSettingsNamespace,
  type KeybindingRule,
  type ServerSettingsPatch,
} from "@cmmd-forge/contracts";
import { useEffect, useRef } from "react";

import { CmmdApiError, cmmdApiClient } from "~/environments/cmmdApi";
import {
  createCmmdUserSettingsSync,
  deriveSparseKeybindings,
  type CmmdUserSettingsApplier,
  type CmmdUserSettingsGateway,
  type CmmdUserSettingsGatewayError,
  type CmmdUserSettingsSync,
} from "~/cmmd/userSettingsSync";
import { getServerConfig, onServerConfigUpdated } from "~/rpc/serverState";
import { getPrimaryEnvironmentConnection } from "~/environments/runtime/service";
import { readCmmdWorkIntakeSnapshot, subscribeCmmdWorkIntake } from "~/cmmd/workIntakeResource";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readLocalDocument(namespace: CmmdUserSettingsNamespace): Record<string, unknown> {
  const config = getServerConfig();
  if (!config) return {};
  if (namespace === "ui-settings") {
    return { ...config.settings };
  }
  return deriveSparseKeybindings(config.keybindings);
}

const isKeybindingCommand = Schema.is(KeybindingCommand);

function sanitizeRemoteBinding(value: unknown): KeybindingRule | null {
  if (!isRecord(value)) return null;
  if (!isKeybindingCommand(value["command"]) || typeof value["key"] !== "string") {
    return null;
  }
  if (typeof value["when"] === "string") {
    return { command: value["command"], key: value["key"], when: value["when"] };
  }
  return { command: value["command"], key: value["key"] };
}

async function applyRemoteDocument(
  namespace: CmmdUserSettingsNamespace,
  document: Record<string, unknown>,
): Promise<void> {
  const client = getPrimaryEnvironmentConnection().client.server;
  if (namespace === "ui-settings") {
    await client.updateSettings(document as ServerSettingsPatch);
    return;
  }

  const remoteBindings = Array.isArray(document["bindings"])
    ? document["bindings"].map(sanitizeRemoteBinding).filter((rule) => rule !== null)
    : [];
  const remoteCommands = new Set(remoteBindings.map((rule) => rule.command));
  const localSparse = deriveSparseKeybindings(getServerConfig()?.keybindings ?? []);

  // Remove local customs the remote no longer has, then apply the remote set.
  // Per-rule RPCs, mirroring what the keybindings settings panel does by hand.
  for (const rule of localSparse.bindings) {
    if (!remoteCommands.has(rule.command)) {
      await client.removeKeybinding({ command: rule.command });
    }
  }
  for (const rule of remoteBindings) {
    await client.upsertKeybinding(rule);
  }
}

function classifyGatewayError(error: unknown): CmmdUserSettingsGatewayError["kind"] {
  if (error instanceof CmmdApiError) {
    if (error.isAuth()) return "auth";
    if (error.status === 404) return "not-found";
    if (error.status === 409) return "conflict";
  }
  return "other";
}

function throwClassified(error: unknown): never {
  throw { kind: classifyGatewayError(error) } satisfies CmmdUserSettingsGatewayError;
}

function createDefaultGateway(): CmmdUserSettingsGateway {
  return {
    get: (namespace) => cmmdApiClient.getUserSettings(namespace).catch(throwClassified),
    put: (namespace, params) =>
      cmmdApiClient.putUserSettings(namespace, params).catch(throwClassified),
  };
}

/**
 * Mount once at the app root. Hydrates both namespaces when CMMD is connected
 * and publishes local changes back while connected. Unauthenticated or
 * disconnected runtimes do nothing: local files stay authoritative.
 */
export function useCmmdUserSettingsSync(): void {
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    let disposed = false;
    let hydrationState: "idle" | "hydrating" | "hydrated" = "idle";
    let hydrationInterrupted = false;
    let sync: CmmdUserSettingsSync | null = null;

    const connected = (): boolean => readCmmdWorkIntakeSnapshot().connected;

    const ensureSync = (): CmmdUserSettingsSync =>
      (sync ??= createCmmdUserSettingsSync({
        gateway: createDefaultGateway(),
        applier: { readLocalDocument, applyRemoteDocument } satisfies CmmdUserSettingsApplier,
        log: {
          warn: (message, context) => console.warn(`[CMMD_USER_SETTINGS] ${message}`, context),
          error: (message, context) => console.error(`[CMMD_USER_SETTINGS] ${message}`, context),
        },
      }));

    const hydrateWhenConnected = (): void => {
      if (disposed || hydrationState !== "idle" || !connected()) return;
      if (getServerConfig() === null) return; // wait for the snapshot first
      hydrationState = "hydrating";
      hydrationInterrupted = false;
      void ensureSync()
        .hydrateAll()
        .then(
          (statuses) => {
            hydrationState =
              !hydrationInterrupted &&
              connected() &&
              [...statuses.values()].every(
                (status) =>
                  status !== "failed" &&
                  status !== "skipped-unauthenticated" &&
                  status !== "skipped-disconnected",
              )
                ? "hydrated"
                : "idle";
          },
          () => {
            hydrationState = "idle";
          },
        )
        .finally(() => {
          // A reconnect may arrive before the interrupted attempt settles.
          if (hydrationInterrupted) hydrateWhenConnected();
        });
    };

    const unsubscribeIntake = subscribeCmmdWorkIntake(() => {
      if (!connected() && hydrationState === "hydrating") hydrationInterrupted = true;
      hydrateWhenConnected();
    });
    hydrateWhenConnected();

    const unsubscribeConfig = onServerConfigUpdated((_, source) => {
      if (disposed || hydrationState === "idle" || hydrationInterrupted || !connected()) return;
      if (source === "settingsUpdated") {
        void ensureSync().publishNamespace("ui-settings");
      } else if (source === "keybindingsUpdated") {
        void ensureSync().publishNamespace("keybindings");
      }
    });

    return () => {
      disposed = true;
      unsubscribeIntake();
      unsubscribeConfig();
      bootstrappedRef.current = false;
    };
  }, []);
}
